/**
*  The abstractInput mixin
*  @module framework/mixins/abstractInput
*/

import abstractElement from './abstractElement';
import { mix, } from '../instance/mix';
import { isFunction, } from '../instance/utils';
import * as manifest  from '../instance/manifest';
import InputError from '../core/InputError';
import { MutationTypes, } from '../instance/store.constants';


/**
* Extends abstractElement with properties that are useful for input elements.
* @mixes framework/mixins/abstractElement
* @mixin
*/
export default mix({}, abstractElement, {
    props: {
        /**
         * Whether this a singular component nested within an aggregate component.
         */
        isNested: Boolean,

        /**
         * Whether is a required data input.
         */
        dataRequired: Boolean,

        /**
         * An array of x-error components used for inline errors in a component.
         */
        dataErrors: Array,

        /**
         * Whether the label should be formatted inline with input.
         */
        inline: String,

        /**
         * Max length of input.
         */
        maxlength: String,

        /**
         * Whether the input adheres to custom width formatting rules.
         */
        customWidth: String,

        /**
         * String for contextual help.
         */
        dataContextualHelp: String,

        /**
         * String for display name for component.
         */
        dataShortName: String,

        /**
         * Id of parent component.  Used if this component is nested in an aggregate component.
         */
        dataParentId: String,

        /**
         * Display name of parent component.
         * Used if this component is nested in an aggregate component.
         */
        dataParentShortName: String,

        /**
         * A regex pattern to validate input.
         */
        dataPattern: String,
        /**
         * A validation function.
         */
        dataOnCustomValidateFunction1: Object,
        /**
         * A second validation function.
         */
        dataOnCustomValidateFunction2: Object,
    },
    data() {
        return {
            /**
             * String for inline error message.
             */
            localErrorMessage: '',

            /**
             * String for error message at error list at top of page.
             */
            globalErrorMessage: '',

            /**
             * Whether component input is in a valid state.
             */
            isValid: false,

            /**
             * Whether component has been interacted with yet on page.
             */
            hasInteraction: false,
        };
    },
    computed: {
        /**
         * The generated id of the route.
         * This is a random string that changes each time the user attempts to navigate
         * away from a route.  It allows us to validate that the route has no errors
         * before allowing navigation away.
         * @returns {String} routeStateId.
         */
        routeStateId() {
            let path = this.$router.history.current.path;
            return this.$store.getters.getRouteStateId(path);
        },

        /**
         * Whether to show errors for the route errors at the top of the page.
         * Errors are not show until the user has tried to navigate away from the page.
         * @returns {boolean} showRouteErrors.
         */
        showRouteErrors() {
            let path = this.$router.history.current.path;
            return this.$store.getters.showRouteErrors(path);
        },

        /**
         * An array of x-error components used for inline errors in a component.
         * @returns {Array} errors.
         */
        errors() {
            if (this.dataErrors) {
                return this.dataErrors;
            }

            return null;
        },

        /**
         * String for display name for component.
         * @returns {String} shortName.
         */
        shortName() {
            return this.getAttribute('shortName', '');
        },

        /**
         * Id of parent component.
         * Used if this component is nested in an aggregate component.
         * @returns {String} parentId.
         */
        parentId() {
            return this.getAttribute('parentId', '');
        },

        /**
         * Display name of parent component.
         * Used if this component is nested in an aggregate component.
         * @returns {String} parentShortName.
         */
        parentShortName() {
            return this.getAttribute('parentShortName', '');
        },

        /**
         * String for contextual help.
         * @returns {String} contextualHelp.
         */
        contextualHelp() {
            return this.getAttribute('contextualHelp', '');
        },

        /**
         * Whether is a required data input.
         * @returns {boolean} required.
         */
        required() {
            if (this.hasPropValue('nodeManifest') && this.nodeManifest.attributes.required) {
                return this.nodeManifest.attributes.required;
            }

            // if no value is specified for a boolean prop, it defaults to false
            return this.dataRequired;
        },
        /**
        * The regex pattern for the input.
        * @returns {*} regex.
        */
        pattern() {
            let patternText = this.getAttribute('pattern', '');
            if (patternText === '') {
                return '';
            }
            patternText.replace(/\\\\/g, '\\');
            let r = new RegExp(patternText);
            return r;
        },

        /**
         * The function that will be called on the OnChange event(if it exists).
         * @returns {function} onChangeFunction.
         */
        onCustomValidateFunction() {
            let validationFunctions = [];
            if (this.dataOnCustomValidateFunction1) {
                validationFunctions.push(this.dataOnCustomValidateFunction1);
            }

            if (this.dataOnCustomValidateFunction2) {
                validationFunctions.push(this.dataOnCustomValidateFunction2);
            }

            if (this.nodeManifest && this.nodeManifest.attributes.onCustomValidateFunction) {
                let functionNames = this.nodeManifest.attributes.onCustomValidateFunction
                    .split(',');
                functionNames.forEach((name) => {
                    let func = this.convertInteractionFunction(name);
                    if (func) {
                        validationFunctions.push(func);
                    }
                });
            }

            return validationFunctions;
        },

        /**
         * The function that will be called on the OnChange event(if it exists).
         * @returns {function} onChangeFunction.
         */
        onChangeFunction() {
            if (this.dataOnChangeFunction) {
                return this.dataOnChangeFunction;
            }

            if (this.nodeManifest && this.nodeManifest.attributes.onChangeFunction) {
                let func = this.convertInteractionFunction(
                    this.nodeManifest.attributes.onChangeFunction);
                if (func) {
                    return func;
                }
            }

            // eslint-disable-next-line no-empty-function
            return () => {};
        },
    },
    watch: {
        /**
         * A watcher watches for the change of a property and calls a function in response.
         *
         * This property is the generated id of the route.
         * This is a random string that changes each time the user attempts to navigate
         * away from a route.  It allows us to validate that the route has no errors
         * before allowing navigation away.
         * @returns {void}
         */
        routeStateId() {
            // this indicates that it is both not nested and not x-dynamic
            if (isFunction(this.getErrorStateOffInputs)) {
                this.handleErrorState();
            }
        },

        /**
         * The values object of the manifest that is passed in as a
         * prop to the component.
         */
        'nodeManifest.values': {
            handler() {
                if (isFunction(this.setComponentValueFromManifest)) {
                    this.setComponentValueFromManifest();
                }
            },
            deep: true,
        },

        /**
         * Set this flag on the manifest to tell this component to update its
         * error state
         * prop to the component.
         */
        'nodeManifest.shouldUpdateError': {
            handler() {
                if (this.nodeManifest.shouldUpdateError) {
                    this.handleErrorState();
                }
            },
        },
    },
    methods: {
        /**
         * Parse the value to a format consumed by the server.
         * This method is a placeholder.
         * It can be overridden by classes that use this mixin.
         * @param {String} val the value.
         * @returns {String} parsed value.
         */
        parse(val) {
            return val;
        },

        /**
         * Format the value to a format displayed on the UI.
         * This method is a placeholder.
         * It can be overridden by classes that use this mixin.
         * @param {String} val the value.
         * @returns {String} parsed value.
         */
        format(val) {
            return val;
        },

        /**
         * Short-circuit the framework data flow if the value is not fully formed.
         * e.g. when the user has enter only the first character of a date.
         * This method is a placeholder.
         * It can be overridden by classes that use this mixin.
         * @param {String} val the value.
         * @returns {String} parsed value.
         */
        // eslint-disable-next-line no-unused-vars
        isBuildingValue(val) {
            return false;
        },

        /**
         * Initialize the component.
         * Get the values(if it exists yet) from manifest or properties
         * Put the values onto the Html inputs
         * @returns {void}
         */
        initialize() {
            if (!this.isNested) {
                if (this.nodeManifest
                    && this.nodeManifest.values
                    && this.nodeManifest.values.length > 0) {
                    this.setComponentValueFromManifest();
                }
            }

            // Complex Inputs won't use this method
            if (isFunction(this.initializeHtmlInputs)) {
                this.initializeHtmlInputs();
            }
        },
        /**
         * Sets the error information from the component onto the appropriate state area.
         * @returns {void}
         */
        handleErrorState() {
            this.hasInteraction = true;
            this.setErrorState();

            let errorMessageObject = this.isValid ? null : {
                localErrorMessage: this.localErrorMessage,
                globalErrorMessage: this.globalErrorMessage,
            };

            if (this.isNested) {
                // broadcast event that error has changed
                // This event is caught by a parent that wants to aggregate its
                // changed error into a complex component
                this.$emit('errorChanged', errorMessageObject);
                return;
            }

            if (!this.isNested) {
                let payload = {
                    id: this.nodeManifest.attributes.id,
                    errorMessageObject,
                };

                this.$store.commit(
                    MutationTypes.UpdateError,
                    payload);
            }
        },
        /**
         * Sets the value from the component to the manifest
         * @param {*} value The value of the component.
         * @returns {void}
         */
        setValueToManifest(value) {
            if (!this.nodeManifest) {
                // this is the case of previewing in the fractal tool.
                return;
            }

            // translate value to the manifest value object shape
            // We have two shapes, Array and Object
            // e.g. checkbox-list gives an array of objects as the value
            // Most others give a single object as the value(we wrap that in an array)
            let val = value;
            if (!Array.isArray(val)) {
                val = [
                    value,
                ];
            }

            this.$store.commit(
                MutationTypes.UpdateValue,
                {
                    id: this.nodeManifest.attributes.id,
                    valuesObject: val,
                });
        },

        /**
         * Gets an id for the control based on the node's manifest
         * @param {*} nodeManifest The node's manifest.
         * @returns {void}
         */
        getFieldId(nodeManifest) {
            return manifest.slugifyFormID(nodeManifest);
        },

        /**
         * Sets the error state on the component
         * @returns {void}
         */
        setErrorState() {
            let validationObject = null;
            let validationMessage = null;
            if (isFunction(this.getErrorStateOffInputs)) {
                let inputErrorState = this.getErrorStateOffInputs();
                validationMessage = inputErrorState.validationMessage;
                validationObject = inputErrorState.validationObject;
            } else {
                validationMessage = '';
                validationObject = InputError.createValidationObject(validationMessage);
            }

            // if basic validations pass, check to see if there are any custom validators
            if (!validationMessage) {
                this.onCustomValidateFunction.forEach((func) => {
                    // once you've done a validation that returns a message that is not
                    // an empty string, don't run any more.
                    if (!validationMessage) {
                        validationMessage = func();
                        validationObject = InputError.createValidationObject(validationMessage);
                    }
                });
            }

            let options = {
                validationObject,
                validationMessage,
                id: this.controlId,
                shortName: this.shortName,
            };

            if (this.isNested) {
                options.parentId = this.parentId;
                options.parentShortName = this.parentShortName;
            }

            let inputError = new InputError(options);
            this.isValid = inputError.isValid;
            this.localErrorMessage = inputError.localMessage;
            this.globalErrorMessage = inputError.globalMessage;
        },

        /**
         * Gets a validation payload to pass into the set error state method.
         * @param {string} message The shared message.
         * @returns {Object} validation payload.
         */
        createValidationPayload(message) {
            let validationObject = InputError.createValidationObject(message);
            return {
                validationObject,
                validationMessage: message,
            };
        },
    },
});



// WEBPACK FOOTER //
// ./source/framework/mixins/abstractInput.js